import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, FileText, FileSpreadsheet, RefreshCw } from "lucide-react";
import { api, API, fmtINR, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const tone = { P: "text-emerald-700", H: "text-amber-700", A: "text-red-700", "-": "text-slate-300" };
const LEGEND = [["P", "Present"], ["H", "Half Day"], ["A", "Absent"], ["-", "Not marked"]];

export default function WorkerAttendanceRegister() {
  const [month, setMonth] = useState(thisMonth());
  const [dept, setDept] = useState("all");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/daily-attendance/register?month=${month}&department=${dept === "all" ? "" : encodeURIComponent(dept)}`);
      setD(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, dept]);

  const download = (kind) => {
    const q = `token=${localStorage.getItem("token")}&month=${month}&department=${dept === "all" ? "" : encodeURIComponent(dept)}`;
    window.open(kind === "pdf" ? `${API}/pdf/worker-attendance-register?${q}` : `${API}/daily-attendance/register/xlsx?${q}`, "_blank");
  };

  const days = Array.from({ length: d?.days_in_month || 0 }, (_, i) => i + 1);
  const t = d?.totals || {};

  return (
    <div data-testid="worker-attendance-register">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <CalendarRange className="w-6 h-6 text-[#0B132B]" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Worker Attendance Register</h1>
            <p className="text-sm text-slate-500 mt-0.5">{d?.month_label || month} · {dept === "all" ? "All departments" : dept} · {d?.rows?.length || 0} worker(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input data-testid="worker-register-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 bg-white" />
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger data-testid="worker-register-department" className="w-44 bg-white h-9"><SelectValue placeholder="All departments" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {(d?.departments || []).map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="worker-register-refresh" size="icon" variant="outline" className="rounded-sm h-9 w-9" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
          <Button data-testid="worker-register-pdf-btn" variant="outline" className="rounded-sm gap-1.5" onClick={() => download("pdf")}><FileText className="w-4 h-4" /> <span className="hidden sm:inline">PDF</span></Button>
          <Button data-testid="worker-register-xlsx-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={() => download("xlsx")}><FileSpreadsheet className="w-4 h-4" /> <span className="hidden sm:inline">Excel</span></Button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap text-xs">
        {LEGEND.map(([k, v]) => (
          <span key={k} className="px-2.5 py-1 bg-white border border-slate-200 rounded-sm">
            <b className={tone[k]}>{k}</b> <span className="text-slate-500">{v}</span>
          </span>))}
        <span className="px-2.5 py-1 bg-white border border-slate-200 rounded-sm text-slate-500">Payable days {t.payable_days ?? 0} · OT {t.overtime_hours ?? 0} hrs · Est. wages <b className="text-slate-800">{fmtINR(t.estimated_wages)}</b></span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 sticky left-0 bg-slate-50">Worker</th>
              {days.map((n) => <th key={n} className="px-1 py-2.5 text-center text-[10px] font-semibold text-slate-500 w-6">{n}</th>)}
              {["P", "H", "A", "NM", "OT", "Days", "Wages"].map((h) => (
                <th key={h} className="px-2 py-2.5 text-center text-[10px] font-semibold uppercase text-slate-500">{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={days.length + 8} className="px-4 py-16 text-center text-slate-400">Loading register...</td></tr>
              : !d?.rows?.length ? <tr><td colSpan={days.length + 8} className="px-4 py-16 text-center text-slate-400" data-testid="worker-register-empty">No active daily workers. Add workers first.</td></tr>
                : d.rows.map((r) => (
                  <tr key={r.worker_id} data-testid={`worker-register-row-${r.worker_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap sticky left-0 bg-white">
                      <div className="font-medium text-slate-800">{r.worker_name}</div>
                      <div className="text-[10px] text-slate-400">{r.worker_code || ""} · {r.job_role}</div>
                    </td>
                    {r.days.map((c, i) => <td key={i} className={`px-1 py-2 text-center font-semibold ${tone[c] || "text-slate-400"}`}>{c}</td>)}
                    <td className="px-2 py-2 text-center font-mono">{r.present_days}</td>
                    <td className="px-2 py-2 text-center font-mono">{r.half_days}</td>
                    <td className="px-2 py-2 text-center font-mono">{r.absent_days}</td>
                    <td className="px-2 py-2 text-center font-mono text-slate-400">{r.unmarked_days}</td>
                    <td className="px-2 py-2 text-center font-mono">{r.overtime_hours}</td>
                    <td className="px-2 py-2 text-center font-mono font-semibold">{r.payable_days}</td>
                    <td className="px-2 py-2 text-right font-mono font-semibold text-slate-800 whitespace-nowrap">{fmtINR(r.estimated_wages)}</td>
                  </tr>))}
            {d?.rows?.length > 0 && (
              <tr className="bg-slate-50 font-semibold border-t-2 border-slate-200" data-testid="worker-register-totals">
                <td className="px-3 py-2 sticky left-0 bg-slate-50">TOTAL</td>
                {days.map((n) => <td key={n} />)}
                <td className="px-2 py-2 text-center font-mono">{t.present_days}</td>
                <td className="px-2 py-2 text-center font-mono">{t.half_days}</td>
                <td className="px-2 py-2 text-center font-mono">{t.absent_days}</td>
                <td className="px-2 py-2 text-center font-mono">{t.unmarked_days}</td>
                <td className="px-2 py-2 text-center font-mono">{t.overtime_hours}</td>
                <td className="px-2 py-2 text-center font-mono">{t.payable_days}</td>
                <td className="px-2 py-2 text-right font-mono whitespace-nowrap">{fmtINR(t.estimated_wages)}</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
